import {
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useState,
  ChangeEventHandler,
} from 'react';

import { CSSObject } from 'styled-components';

import {
  getShouldRenderCount,
  omitKeys,
  type CharacterCountLimiterProps,
} from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import { StylableComponent } from '@adsk/alloy-react-theme';

import InlineEditContext from './InlineEditContext';
import { ValueType } from './types';

type UseInlineInputProps<TElement, TValue> = {
  inputRef: RefObject<TElement>;
  value: TValue;
  error?: boolean;
  isTextArea?: boolean;
  saveUnmodifiedValue?: boolean;
  cancelOnBlur?: boolean;
} & CharacterCountLimiterProps;

export default <
  TElement extends HTMLInputElement | HTMLTextAreaElement,
  TValue extends ValueType,
>({
  inputRef,
  value,
  isTextArea = false,
  saveUnmodifiedValue = false,
  cancelOnBlur = false,
  error = false,
  softLimit,
  maxLength,
  showCount,
  showCountThreshold = 15,
}: UseInlineInputProps<TElement, TValue>) => {
  const [fieldValue, setFieldValue] = useState<TValue>(value);
  const [modified, setModified] = useState<boolean>(saveUnmodifiedValue);
  const [charCount, setCharCount] = useState(0);
  const inlineEdit = useContext(InlineEditContext);
  const { tokens } = useTheme();
  const inputWrapperTokens = useMemo(() => {
    const inputWrapper = tokens?.component.inlineEdit?.inputWrapper;
    if (!inputWrapper) {
      return undefined;
    }
    if (!isTextArea) {
      return inputWrapper._;
    }
    const textAreaVariant = inputWrapper.isTextArea?.true;
    if (!textAreaVariant) {
      return inputWrapper._;
    }
    return [inputWrapper._, textAreaVariant];
  }, [tokens, isTextArea]);
  const inputTokens = tokens?.component.inlineEdit?.input?._;
  const characterCountTokens = tokens?.component.inlineEdit?.characterCount;
  const editButtonTokens = tokens?.component.inlineEdit?.editButton;

  useEffect(() => {
    if (typeof value === 'string') {
      setCharCount(value.length);
    } else {
      setCharCount(0);
    }
  }, [value]);

  const shouldRenderCount = useMemo(
    () =>
      getShouldRenderCount({
        showCount,
        maxLength,
        softLimit,
        charCount,
        showCountThreshold,
      }),

    [showCount, maxLength, softLimit, charCount, showCountThreshold],
  );

  const handleSubmit = () => {
    if (!error) {
      setModified(false);
      inlineEdit.updateValue?.(fieldValue);
    }
  };

  const handleCancel = () => {
    setModified(false);
    setFieldValue(value);
    inlineEdit.setEditMode?.(false);
    inlineEdit.onEditExit?.(value, { canceled: true });

    // setFieldValue doesn't cause onChange to fire, so let's trigger it
    // https://stackoverflow.com/a/46012210/2883129
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window[isTextArea ? 'HTMLTextAreaElement' : 'HTMLInputElement'].prototype,
      'value',
    )?.set;
    nativeInputValueSetter?.call(inputRef.current, value);

    const event = new Event('input', { bubbles: true });
    inputRef.current?.dispatchEvent(event);
  };

  const onChange: ChangeEventHandler<TElement> = (e) => {
    const newValue = e.target.value as TValue;
    setFieldValue(newValue);
    setModified(newValue !== value);
    if (typeof newValue === 'string') {
      setCharCount(newValue.length);
    }
    inlineEdit.inputOnChange?.(e);
  };

  // To prevent submission with 'Enter' when IME is active (for languages like Japanese or Chinese).
  // keyCode 229 is the IME input key for old browsers
  const isInIMEComposition = (e: React.KeyboardEvent<TElement>) => {
    return e.nativeEvent.isComposing || e.keyCode === 229;
  };

  const onKeyDown: React.KeyboardEventHandler<TElement> = (e) => {
    if (e.key === 'Enter' && !isInIMEComposition(e)) {
      handleSubmit();
      e.preventDefault();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return {
    getWrapperProps: ({
      style = {},
      ...props
    }: StylableComponent<HTMLElement>) => ({
      onBlur: cancelOnBlur ? handleCancel : handleSubmit,
      style: style as CSSObject,
      ...props,
    }),
    getInputProps: <T>({
      style = {},
      ...props
    }: StylableComponent<TElement> & T & CharacterCountLimiterProps) => ({
      value: fieldValue,
      ref: inputRef,
      autoFocus: true,
      dir: 'auto',
      style: style as CSSObject,
      onChange,
      onKeyDown,
      ...omitKeys(props, [
        'showCount',
        'showCount',
        'showCountThreshold',
        'softLimit',
      ]),
    }),
    modified,
    handleSubmit,
    handleCancel,
    onChange,
    onKeyDown,
    charCount,
    shouldRenderCount,
    inputWrapperTokens,
    inputTokens,
    characterCountTokens,
    editButtonTokens,
  };
};
